import type { AccountEntityExpandResponse } from "@/shared/generated/ExpHubApi";
import { type AccountAddress, mapToAccountAddressType } from "./AccountAddress";
import {
  mapToAccountEmailAddressType,
  type AccountEmailAddress,
} from "./AccountEmailAddress";
import { InvoiceEmailVariant } from "@/app/gas-shop-app/email-addresses/@types/InvoiceEmailVariant";
import { AddressType } from "./AddressType";
import type { CustomerAdditionalInfo } from "./CustomerAdditionalInfo";
import { ContactAddressType } from "./ContactAddressType";
import type { CustomerBase } from "./CustomerBase";
import { mapToCustomerTypeEnum, type CustomerType } from "./CustomerType";
import { ServiceExternalIdentifier } from "./ServiceExternalIdentifier";
import {
  mapToServiceBusinessAreaKeyEnum,
  type ServiceBusinessAreaKey,
} from "@/shared/@types/ServiceBusinessAreaKey";
import { mapToUserLocaleCode, UserLocaleCode } from "./UserLocaleCode";
import { mapToWebStore, type WebStore } from "./WebStore";

export interface Customer extends Omit<CustomerBase, "user"> {
  // TODO: Remove user object and add userId when language is provided by user itself
  user: {
    id: string;
    localeCode?: UserLocaleCode;
  };
  webStores: Array<WebStore>;
  assignedBusinessAreas: Array<ServiceBusinessAreaKey>;
  type?: CustomerType;
  shippingAddresses?: Array<AccountAddress>;
  billingAddresses?: Array<AccountAddress>;
  invoiceEmail?: AccountEmailAddress;
  deliveryNoteEmails?: Array<AccountEmailAddress>;
  safetyDataEmails?: Array<AccountEmailAddress>;
  additionalInfo?: CustomerAdditionalInfo;
  // Whether a Westfalen.ID account manager is already assigned to the customer
  isManaged?: boolean;
}

export const mapToCustomerType = (
  data?: AccountEntityExpandResponse,
  baseData?: CustomerBase,
): Customer => {
  if (!data && !baseData) {
    throw new Error(
      "Either data or baseData must be provided to map to Customer type.",
    );
  }
  /* Use baseData for customerNumber if available, otherwise fallback to data and replace
   * leading zeros in the ERP customer id to match the number our backend provides */
  const customerNumber =
    baseData?.number ?? data?.account.ERPCustomerId?.replace(/^0+/, "");

  const customerBase: Customer = {
    id: baseData?.id ?? data?.account.accountId ?? "",
    name: baseData?.name ?? data?.account.name ?? "",
    name2: baseData?.name2 ?? undefined,
    number: customerNumber,
    user: {
      id: baseData?.user.id ?? data?.user.userId ?? "",
    },
    webStores: [],
    assignedBusinessAreas: [],
  };

  if (!data) return customerBase;

  const filteredShippingAddresses =
    data.addresses?.filter(
      (address) => address.kind === AddressType.Shipping,
    ) || [];
  const filteredBillingAddresses =
    data.addresses?.filter((address) => address.kind === AddressType.Billing) ||
    [];

  // The invoice email address can be either of type INVOICE or one of the InvoiceEmailVariants, so we need to check for both.
  // There should always be only one invoice email address, so we can use find() to get the first one that matches.
  const invoiceEmailVariantValues = Object.values(
    InvoiceEmailVariant,
  ) as string[];
  const invoiceEmailAddress = data.contactAddresses?.find(
    (address) =>
      address.kind === ContactAddressType.Invoice ||
      invoiceEmailVariantValues.includes(address.kind),
  );
  const filteredDeliveryNoteEmailAddresses =
    data.contactAddresses?.filter(
      (address) => address.kind === ContactAddressType.DeliveryNote,
    ) || [];
  const filteredSafetyDataEmailAddresses =
    data.contactAddresses?.filter(
      (address) => address.kind === ContactAddressType.SafetyData,
    ) || [];
  const palletConfigurationInfo = data.account.services?.find(
    (service) =>
      service.serviceExternalId === ServiceExternalIdentifier.GlobalGasShop,
  )?.subscription;

  return {
    ...customerBase,
    user: {
      id: customerBase.user.id,
      localeCode: mapToUserLocaleCode(data.user.language) || UserLocaleCode.DE,
    },
    webStores:
      data.account.webstores
        ?.map(mapToWebStore)
        .filter((webStore): webStore is WebStore => webStore !== undefined) ||
      [],
    assignedBusinessAreas:
      data.account.businessAreasSummary?.map(mapToServiceBusinessAreaKeyEnum) ||
      [],
    type: mapToCustomerTypeEnum(data.account.customerType),
    shippingAddresses: filteredShippingAddresses.map(mapToAccountAddressType),
    billingAddresses: filteredBillingAddresses.map(mapToAccountAddressType),
    invoiceEmail: invoiceEmailAddress
      ? mapToAccountEmailAddressType(invoiceEmailAddress)
      : undefined,
    deliveryNoteEmails: filteredDeliveryNoteEmailAddresses.map(
      mapToAccountEmailAddressType,
    ),
    safetyDataEmails: filteredSafetyDataEmailAddresses.map(
      mapToAccountEmailAddressType,
    ),
    additionalInfo: {
      palletConfiguration: {
        minAmountOfPallets: palletConfigurationInfo?.minimalAmountOfPallets,
      },
    },
    isManaged: data.account.isManaged,
  };
};
